import {Button, ButtonActions} from "/@back@/commons/widgets/buttons.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IDiffBarAction, IDiffBarPanel, IWedDiffBar, IWedEditorDiffBar, JWedDiffBarParams, OWedEditorDiffBarConfig} from "/@back@/edit/wed/features/diffBar.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {findWedEditor, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {IView, VIEWS} from "/@lib@/commons/views.js";
import {WedSearchBarNextPrev} from "/@back@/edit/wed/features/searchBar.js";

/**
 * Widget principal de la diffBar.
 */
export class WedDiffBar extends HTMLElement implements IWedDiffBar {
	editor: IWedEditorDiffBar;

	diffActions: IDiffBarAction[];

	protected selector: ButtonActions<WedDiffBar>;
	protected eraseBtn: Button;
	protected refreshBtn: Button;
	protected prevNextBtns: WedSearchBarNextPrev;
	protected rootPanels: HTMLElement;

	currentPanel: IDiffBarPanel | null;

	constructor() {
		super();
		this.id = "diffBar";
		this.hidden = true;
	}

	init(editor: IWedEditorDiffBar): this {
		this.editor = editor;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const reg = editor.reg;
		reg.installSkin(this.localName, sr);

		this.diffActions = reg.getList("wed.diffBar.actions");
		this.selector = new ButtonActions().initialize({
			reg,
			actions: this.diffActions,
			actionContext: this
		}) as ButtonActions<WedDiffBar>;

		//TODO <c-button title="Différence précédente" icon="/@skin@/edit/wed/search/prev.svg" onclick={this.onPrevious}/>
		//TODO <c-button title="Différence suivante" icon="/@skin@/edit/wed/search/next.svg" onclick={this.onNext}/>
		this.prevNextBtns = new WedSearchBarNextPrev();
		this.prevNextBtns.annotFilter = WEDLET.diffLib.isDiffAnnot;
		this.eraseBtn = <Button title="Effacer les différences" icon="/@skin@/edit/wed/diff/erase.svg" onclick={this.onErase} disabled/> as Button;
		this.refreshBtn = <Button title="Rafraîchir les différences" icon="/@skin@/commons/icons/refresh.svg" onclick={this.onRefetchDiff} disabled/> as Button;
		const closeBtn = (editor.config as OWedEditorDiffBarConfig).diffBarMode !== 'shown' ? <c-button icon="/@skin@/commons/icons/close.svg" title="Fermer" onclick={this.onClose}/> : null;

		sr.appendChild(<div id="top">
			<div id="head">
				<span>Comparer avec</span>
				{this.selector}
				<div id="buttons">
					{this.prevNextBtns}
					{this.eraseBtn}
					{this.refreshBtn}
				</div>
			</div>
			{closeBtn}
		</div>);
		this.rootPanels = sr.appendChild(<div id="panelCtn"/>);

		editor.wedMgr.listeners.on("shown", (wedMgr: WedMgr) => {VIEWS.onViewShown(this.currentPanel)});
		editor.wedMgr.listeners.on("hidden", (wedMgr: WedMgr, close?: boolean) => {VIEWS.onViewHidden(this.currentPanel, close)});

		this.refresh();
		return this;
	}

	/** Paramètres d'init d'un panel. */
	initParams: JWedDiffBarParams | null;

	getDiffParams(): JWedDiffBarParams | null {
		return !this.hidden && this.currentPanel ? this.currentPanel.buildDiffParams() : null;
	}

	/** */
	async setDiffParams(params: JWedDiffBarParams) {
		if (!WEDLET.diffLib) await WEDLET.importDiffLib();
		if (this.hidden) {
			this.editor.barLayout.showBar(this);
			this.hidden = false;
		}
		if (this.currentPanel?.action.getId() !== params.diffId) {
			const act = this.diffActions.find((a) => a.getId() === params.diffId);
			if (!act) return;
			try {
				this.initParams = params;
				await act.execute(this);
			} finally {
				this.initParams = null;
			}
		} else {
			this.currentPanel.setDiff(params);
		}
	}

	close() {
		this.hidden = true;
		this.editor.barLayout.onHideBar(this);
		this.setCurrentPanel(null);
	}

	show() {
		this.hidden = false;
		this.editor.barLayout.showBar(this);
		this.refresh();
	}

	setCurrentPanel(panel: IDiffBarPanel | null) {
		if (panel && panel.parentNode !== this.rootPanels) {
			//1ère déclaration, on ajoute.
			this.rootPanels.append(panel);
			panel.hidden = true;
		}
		//On masque et déconnecte tous les autres, on affiche et connecte ce panel.
		const wedMgr = this.editor.wedMgr;
		for (let ch = this.rootPanels.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (DOM.setHidden(ch, ch !== panel)) {
				if (ch !== panel) {
					(ch as IDiffBarPanel).unconnectDoc();
					VIEWS.onViewHidden(ch as IView, false);
				} else if (wedMgr.docHolderAsync) (ch as IDiffBarPanel).connectDoc();
				else this.editor.wedMgr.listeners.once("redrawAtEnd", () => {(ch as IDiffBarPanel).connectDoc()});
			}
		}
		this.selector.label = panel ? panel.action.getLabel(this) : "";
		//this.selector.icon = panel ? panel.action.getIcon(this) : "";
		this.currentPanel = panel;
		if (panel) VIEWS.onViewShown(panel);
		this.dispatchDiffChange();
	}

	setDiffStates(st: 'drawn' | 'computable' | 'notComputable') {
		switch (st) {
		case "drawn":
			DOM.setAttrBool(this.eraseBtn, 'disabled', false);
			DOM.setAttrBool(this.refreshBtn, 'disabled', false);
			this.prevNextBtns.setDisabled(false);
			break;
		case "computable":
			DOM.setAttrBool(this.eraseBtn, 'disabled', true);
			DOM.setAttrBool(this.refreshBtn, 'disabled', false);
			this.prevNextBtns.setDisabled(true);
			break;
		case "notComputable":
			DOM.setAttrBool(this.eraseBtn, 'disabled', true);
			DOM.setAttrBool(this.refreshBtn, 'disabled', true);
			this.prevNextBtns.setDisabled(true);
			break;
		}
	}

	getSearchParams(): JWedDiffBarParams | null {
		return !this.hidden && this.currentPanel ? this.currentPanel.buildDiffParams() : null;
	}

	dispatchDiffChange() {
		// const coord = (this.editor.reg.env as IWedDiffCoordinatorPointer).wedDiffCoord;
		// if (coord) coord.setParams(this.currentPanel?.buildDiff() || null);
	}

	getPanelFor(action: IDiffBarAction): IDiffBarPanel | null {
		for (let ch = this.rootPanels.firstElementChild; ch; ch = ch.nextElementSibling) {
			if ((ch as IDiffBarPanel).action === action) return ch as IDiffBarPanel;
		}
		return null;
	}

	protected onErase(this: Button) {
		DOMSH.findHost<WedDiffBar>(this).currentPanel?.setDiff(null);
	}

	protected onRefetchDiff(this: Button) {
		DOMSH.findHost<WedDiffBar>(this).currentPanel?.execDiff(true);
	}

	protected onClose(this: Button) {
		DOMSH.findHost<WedDiffBar>(this).close();
	}


	refresh() {
		if (this.hidden) return;
		if (!this.currentPanel) {
			if (this.diffActions) {
				for (let action of this.diffActions) {
					if (action.isAvailable(this)) {
						action.execute(this);
						break;
					}
				}
			}
		} else {
			this.currentPanel.action.execute(this); //refresh.
		}
	}
}

REG.reg.registerSkin("wed-diffbar", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-width: 0;
		flex-direction: column;
		background-color: var(--bgcolor);
		font-size: var(--label-size);
	}

	[hidden] {
		display: none !important;
	}

  #top {
	  display: flex;
	  min-height: 1.5em;
  }

  #head {
	  flex: 1;
	  display: flex;
	  flex-wrap: wrap;
	  align-items: center;
	  padding: 0 .3em;
  }

  #buttons {
	  display: flex;
  }

  c-button-actions {
	  flex: 1 1 auto;
	  padding: 0;
	  padding-inline-start: 1.2em;
	  background-position: left;
	  align-self: stretch;
	  justify-content: start;
	  font-weight: bold;
  }

  #panelCtn {
	  flex: 1;
  }
`);
customElements.define("wed-diffbar", WedDiffBar);


/** Classe de base pour les panels de diff dans la barre de recherche. */
export abstract class DiffPanelBase<ENV = {}> extends HTMLElement implements IDiffBarPanel {
	action: IDiffBarAction;
	bar: IWedDiffBar;
	wedMgr: WedMgr;
	reg: IReg<ENV>;

	docConnected: boolean;
	result: HTMLElement;

	protected _initPanel(action: IDiffBarAction, bar: IWedDiffBar) {
		this.action = action;
		this.bar = bar;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.wedMgr = findWedEditor(bar).wedMgr;
		this.reg = this.wedMgr.reg;
		this.reg.installSkin(this.localName, sr);
		this.result = <span id="result"/> as HTMLElement;
		this._needRefresh = this.needRefresh.bind(this);
		this.docConnected = false;
	}

	connectDoc(): void {
		this.wedMgr.listeners.on("hookEndTransac", this._needRefresh);
		this.docConnected = true;
		//this.execDiff();
	}

	unconnectDoc(): void {
		this.setDiff(null);
		if (this.docConnected) {
			this.wedMgr.listeners.removeListener("hookEndTransac", this._needRefresh);
			this.wedMgr.docHolder.setDiffSession(null);
			this.docConnected = false;
		}
	}

	abstract setDiff(params: JWedDiffBarParams | null): void;

	buildDiffParams(): JWedDiffBarParams {
		return {diffId: this.action.getId()}
	}

	updateDiffResult(count: number | 'computing' | 'needRefresh' | 'error' | 'notFound' | 'computable' | null) {
		if (count == null) {
			DOM.setTextContent(this.result, null);
			this.bar.setDiffStates("notComputable");
			return;
		}
		switch (count) {
		case "needRefresh":
			if (!this.result.firstElementChild) {
				DOM.setTextContent(this.result, null);
				this.result.appendChild(<a onclick={this.onRefresh} href="">Rafraîchir</a>);
			}
			this.bar.setDiffStates("drawn");
			break;
		case "computing":
			DOM.setTextContent(this.result, "Recherche en cours...");
			this.bar.setDiffStates("notComputable");
			break;
		case "error":
			DOM.setTextContent(this.result, "Recherche de différences impossible");
			this.bar.setDiffStates("drawn");
			break;
		case "notFound":
			DOM.setTextContent(this.result, "Autre source de comparaison indisponible");
			this.bar.setDiffStates("drawn");
			break;
		case "computable":
			DOM.setTextContent(this.result, null);
			this.bar.setDiffStates("computable");
			break;
		case 0:
			DOM.setTextContent(this.result, "➔ Aucune différence significative trouvée");
			this.bar.setDiffStates("drawn");
			break;
		case 1:
			DOM.setTextContent(this.result, "➔ 1 différence trouvée");
			this.bar.setDiffStates("drawn");
			break;
		default:
			DOM.setTextContent(this.result, `➔ ${count} différences trouvées`);
			this.bar.setDiffStates("drawn");
			break;
		}
	}

	protected onRefresh(this: HTMLAnchorElement, ev: Event) {
		ev.preventDefault();
		const me = DOMSH.findHost<DiffPanelBase>(this);
		me.execDiff();
	}

	abstract execDiff(fullRefresh?: boolean): Promise<void>;

	_needRefresh: () => void;

	protected needRefresh(): void {
		this.updateDiffResult('needRefresh');
	}
}